"use client";

import { NavArrowDown } from "iconoir-react";
import { useLocale } from "next-intl";
import type { CSSProperties } from "react";

import {
  formatLocalizedAmountParts,
  localizeCurrencyString,
} from "@/lib/localize-currency";
import { cn } from "@/lib/utils";

export interface MetricPanelCardProps {
  /** Header label, e.g. "Monthly Revenue" or "Available funds". */
  title: string;
  /** Numeric amount rendered with locale currency formatting. */
  amount: number;
  /** Green trend copy, e.g. "Above $10k". Pass pre-localized text. */
  trendLabel: string;
  /** Sparkline SVG path under public/. */
  sparklineSrc: string;
  className?: string;
  style?: CSSProperties;
  "data-testid"?: string;
}

/** Inner white metric card — Figma 8301:73195 / 8444:61094. */
export function MetricPanelCard({
  title,
  amount,
  trendLabel,
  sparklineSrc,
  className,
  style,
  "data-testid": dataTestId,
}: MetricPanelCardProps) {
  const locale = useLocale();
  const formatted = formatLocalizedAmountParts(amount, locale);

  return (
    <div
      data-testid={dataTestId}
      style={style}
      className={cn(
        "relative overflow-hidden rounded-[12px] bg-white shadow-[0px_4.827px_24.134px_0px_rgba(0,0,0,0.12)]",
        className
      )}
    >
      <div className="absolute top-[15px] left-[18px] flex w-[calc(100%-36px)] items-start gap-[6px]">
        <p className="min-w-0 flex-1 truncate text-[12.2px] leading-[1.4] font-medium text-[#0d0d0d]">
          {title}
        </p>
        <span className="inline-flex shrink-0 items-center gap-[2px] rounded-full border border-[#e6e6e5] px-[6px] py-[2px] text-[9px] leading-none font-medium text-[#0d0d0d]">
          L30D
          <NavArrowDown className="size-2" strokeWidth={2} aria-hidden />
        </span>
      </div>

      <div className="absolute top-[46px] left-[18px] w-[calc(100%-36px)]">
        <div className="flex items-center gap-[1.5px] text-[28px] leading-[1.2] tracking-[-0.38px] whitespace-nowrap">
          {formatted.placement === "before" ? (
            <span className="shrink-0 text-[#b0b3b5]">{formatted.symbol}</span>
          ) : null}
          <span className="shrink-0 text-[#0d0d0d]">{formatted.amount}</span>
          {formatted.placement === "after" ? (
            <span className="shrink-0 text-[#b0b3b5]">{formatted.symbol}</span>
          ) : null}
        </div>
        <p className="mt-[3px] flex items-center gap-[3px] text-[10.7px] leading-none whitespace-nowrap text-[#29a347]">
          <span>↑</span>
          <span>{trendLabel}</span>
        </p>
      </div>

      <img
        src={sparklineSrc}
        alt=""
        className="absolute top-[101px] left-[18px] h-[24px] w-[calc(100%-36px)] max-w-[297px]"
        aria-hidden
      />
    </div>
  );
}

/** Preset for growing / early-stage revenue criterion inset. */
export function RevenueMetricPanelCard({ className }: { className?: string }) {
  const locale = useLocale();

  return (
    <MetricPanelCard
      title="Monthly Revenue"
      amount={12_480.05}
      trendLabel={localizeCurrencyString("Above $10k", locale)}
      sparklineSrc="/assets-next/growing/eligibility/revenue-sparkline.svg"
      className={className}
      data-testid="revenue-metric-panel-card"
    />
  );
}

/** Preset for DTC limited-risk available funds inset. */
export function AvailableFundsMetricPanelCard({
  className,
  style,
}: {
  className?: string;
  style?: CSSProperties;
}) {
  const locale = useLocale();

  return (
    <MetricPanelCard
      title="Available funds"
      amount={320_180}
      trendLabel={localizeCurrencyString("Above $100k", locale)}
      sparklineSrc="/assets-next/cards/limited-risk/available-funds-sparkline.svg"
      className={className}
      style={style}
      data-testid="available-funds-metric-panel-card"
    />
  );
}
